/**
 * Eqaya Platform — Centralized Color Tokens
 *
 * Palette: Deep Teal + Warm Earth Tones
 * Inspired by: African earth, construction materials, trust & sustainability
 *
 * Import from here instead of hardcoding hex values.
 * Usage:  import { colors } from '../theme/colors';
 *         sx={{ color: colors.text.primary, background: colors.brand.gradient }}
 */

// ── Brand ────────────────────────────────────────────────────────────────
// Primary: Deep teal (trust, stability, growth)
// Secondary: Warm terracotta (African earth, construction, warmth)
// Accent: Burnished gold (premium, milestones, achievements)
export const brand = {
  primary:        '#0d9488',   // Teal 600 — main brand color
  primaryLight:   '#5eead4',   // Teal 300 — light variant
  primaryDark:    '#0f766e',   // Teal 700 — dark variant
  primaryDeep:    '#134e4a',   // Teal 900 — deepest, for dark backgrounds
  secondary:      '#c2410c',   // Terracotta/burnt orange — warm earth accent
  secondaryLight: '#fb923c',   // Orange 400 — lighter earth
  secondaryDark:  '#9a3412',   // Orange 800 — deep earth
  accent:         '#d97706',   // Amber 600 — gold for badges, CTAs, premium
  accentLight:    '#fbbf24',   // Amber 400 — lighter gold
  gradient:       'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
  gradientHover:  'linear-gradient(135deg, #0f766e 0%, #0c3f3a 100%)',
  gradientWarm:   'linear-gradient(135deg, #0d9488 0%, #c2410c 100%)',
  gradientEarth:  'linear-gradient(135deg, #c2410c 0%, #d97706 100%)',
};

// ── Semantic / Status ────────────────────────────────────────────────────
export const status = {
  success:     '#16a34a',   // Green 600
  successDark: '#15803d',   // Green 700
  successBg:   'rgba(22, 163, 74, 0.08)',

  warning:     '#d97706',   // Amber 600
  warningDark: '#b45309',   // Amber 700
  warningBg:   'rgba(217, 119, 6, 0.08)',

  error:       '#dc2626',   // Red 600
  errorDark:   '#b91c1c',   // Red 700
  errorBg:     'rgba(220, 38, 38, 0.08)',

  info:        '#2563eb',   // Blue 600
  infoDark:    '#1d4ed8',   // Blue 700
  infoBg:      'rgba(37, 99, 235, 0.08)',

  neutral:     '#6b7280',   // Gray 500
  neutralBg:   'rgba(107, 114, 128, 0.08)',
};

// ── Verifier role accent ─────────────────────────────────────────────────
export const verifier = {
  primary:   '#0d9488',   // Same as brand primary (verifiers are core)
  dark:      '#0f766e',
  light:     '#ccfbf1',   // Teal 100
  gradient:  'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
};

// ── Text ─────────────────────────────────────────────────────────────────
export const text = {
  primary:   '#1e293b',   // Slate 800 — stronger, more modern than #2c3e50
  secondary: '#64748b',   // Slate 500
  tertiary:  '#94a3b8',   // Slate 400
  disabled:  '#cbd5e1',   // Slate 300
  inverse:   '#ffffff',
};

// ── Backgrounds ──────────────────────────────────────────────────────────
export const bg = {
  page:      '#f8fafc',   // Slate 50 — slightly warmer than #f5f7fa
  pageLight: '#fdfefe',   // Near-white — for surfaces where panels carry their
                          // own shadow and `page` sat too close to `subtle`
  paper:     '#ffffff',
  elevated:  '#ffffff',
  subtle:    '#f1f5f9',   // Slate 100
  header:    '#eef2f7',   // Panel/table header fill — a step darker than
                          // `subtle` so it reads against `pageLight`
  hover:     '#e2e8f0',   // Slate 200
  selected:  'rgba(13, 148, 136, 0.06)',   // Teal tint
  overlay:   'rgba(0, 0, 0, 0.5)',
  earth:     '#fef7ed',   // Warm earth background (for CTAs, banners)
};

// ── Borders ──────────────────────────────────────────────────────────────
export const border = {
  light:   '#e2e8f0',   // Slate 200
  default: '#cbd5e1',   // Slate 300
  strong:  '#94a3b8',   // Slate 400
  focus:   '#0d9488',   // Teal — matches brand
};

// ── Project status (matches backend enums) ───────────────────────────────
export const projectStatus = {
  planning:      '#6b7280',   // Gray 500
  open_for_bids: '#2563eb',   // Blue 600
  in_progress:   '#d97706',   // Amber 600
  on_hold:       '#b45309',   // Amber 700
  completed:     '#16a34a',   // Green 600
  cancelled:     '#dc2626',   // Red 600
};

// ── Verification status ──────────────────────────────────────────────────
export const verificationStatus = {
  pending:     '#d97706',   // Amber 600
  assigned:    '#2563eb',   // Blue 600
  in_progress: '#0d9488',   // Teal 600
  completed:   '#16a34a',   // Green 600
  approved:    '#15803d',   // Green 700
  cancelled:   '#dc2626',   // Red 600
  disputed:    '#c2410c',   // Orange 700
};

// ── Priority ─────────────────────────────────────────────────────────────
export const priority = {
  low:      '#6b7280',   // Gray 500
  normal:   '#2563eb',   // Blue 600
  high:     '#d97706',   // Amber 600
  critical: '#dc2626',   // Red 600
};

// ── Milestone types ──────────────────────────────────────────────────────
export const milestoneType = {
  foundation: '#78716c',   // Stone 500 — concrete/stone
  framing:    '#d97706',   // Amber 600 — wood/timber
  roofing:    '#dc2626',   // Red 600 — roofing tiles
  electrical: '#eab308',   // Yellow 500 — electrical safety
  plumbing:   '#2563eb',   // Blue 600 — water
  finishing:  '#7c3aed',   // Violet 600 — interior design
  final:      '#16a34a',   // Green 600 — completion
};

// ── Charts / Data Visualization ──────────────────────────────────────────
export const chart = {
  palette: ['#0d9488', '#c2410c', '#d97706', '#2563eb', '#16a34a', '#dc2626', '#7c3aed', '#0891b2'],
  gradients: {
    primary:  ['#0d9488', '#134e4a'],
    warm:     ['#c2410c', '#d97706'],
    success:  ['#16a34a', '#4ade80'],
    warning:  ['#d97706', '#fbbf24'],
    error:    ['#dc2626', '#f87171'],
    info:     ['#2563eb', '#60a5fa'],
    earth:    ['#c2410c', '#78716c'],
  },
};

// ── Shadows (tinted with brand teal) ─────────────────────────────────────
export const shadow = {
  xs:    '0 1px 2px rgba(13, 148, 136, 0.06)',
  sm:    '0 1px 3px rgba(13, 148, 136, 0.08), 0 1px 2px rgba(13, 148, 136, 0.06)',
  md:    '0 4px 12px rgba(13, 148, 136, 0.12), 0 2px 4px rgba(13, 148, 136, 0.08)',
  lg:    '0 8px 24px rgba(13, 148, 136, 0.16), 0 4px 8px rgba(13, 148, 136, 0.10)',
  xl:    '0 16px 40px rgba(13, 148, 136, 0.20), 0 8px 16px rgba(13, 148, 136, 0.12)',
  hover: '0 8px 24px rgba(13, 148, 136, 0.16), 0 4px 8px rgba(13, 148, 136, 0.10)',
  warm:  '0 4px 12px rgba(194, 65, 12, 0.12), 0 2px 4px rgba(194, 65, 12, 0.08)',
};

// ── Easing tokens ────────────────────────────────────────────────────────
export const easing = {
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
  decel:  'cubic-bezier(0, 0, 0.2, 1)',
};

// ── Convenience: combined export ─────────────────────────────────────────
export const colors = {
  brand,
  status,
  verifier,
  text,
  bg,
  border,
  projectStatus,
  verificationStatus,
  priority,
  milestoneType,
  chart,
  shadow,
  easing,
};

export default colors;
